import { EditorSDK, EditorReadyOptions } from '@wix/platform-editor-sdk';
import { IFedOpsLogger } from '@wix/native-components-infra/dist/src/types/types';
import {
  EcomComponent,
  addComponents,
  installCartIcon,
  EcomComponentConfiguration,
} from '@wix/ecom-platform-sdk';
import { isEcomCartInstalled } from '../editor-sdk-actions';
import { EditorScriptApi } from '../../api/api';
import {
  updateIsCartEnabledBusinessProperty,
  getIsCartEnabledBusinessProperty,
} from '../migrate-actions';
import { openCartSuccessfullyInstalledModal } from '../migration-modals';
import { Actions, reportBi } from '../editor-script-bi-logger';

interface MigrateCartParams {
  editorSdk: EditorSDK;
  editorScriptApi: EditorScriptApi;
  instance: string;
}

interface InstallCart {
  editorSdk: EditorSDK;
  editorScriptApi: EditorScriptApi;
  instance: string;
  msid: string;
  editorOrigin: string;
  editorOptions: EditorReadyOptions;
  fedopsLogger: IFedOpsLogger;
  locale?: any;
}

interface MarkCartEnabled {
  editorSdk: EditorSDK;
  editorScriptApi: EditorScriptApi;
  isFirstInstall: boolean;
  instance: string;
  msid: string;
  editorOrigin: string;
  fedopsLogger: IFedOpsLogger;
}

export const requiredEcomComponentsAction = (
  editorReadyOptions: EditorReadyOptions,
): (() => Promise<EcomComponentConfiguration>) => {
  return async (): Promise<EcomComponentConfiguration> => {
    return {
      shouldInstallMembersArea: true,
      ecomComponents: {
        [EcomComponent.CHECKOUT]: {
          isInstalled: true,
        },
        [EcomComponent.THANK_YOU_PAGE]: {
          isInstalled: true,
        },
        [EcomComponent.CART]: {
          isInstalled: true,
        },
        [EcomComponent.CART_ICON]: {
          isInstalled: true,
        },
      },
    };
  };
};

export const installEcomCartIcon = async ({
  editorSdk,
  editorOptions,
  editorScriptApi,
  locale,
  instance,
}: InstallCart) => {
  if (editorOptions?.appUrlQueryParams?.isCartFirstInstall) {
    if (!editorOptions?.firstInstall) {
      await installCartIcon(editorSdk);
    }

    openCartSuccessfullyInstalledModal(
      editorSdk,
      editorScriptApi,
      locale,
      instance,
    );
  }
};

export const installCart = async ({
  editorOptions,
  editorScriptApi,
  instance,
  msid,
  locale,
  editorSdk,
  fedopsLogger,
  editorOrigin,
}: InstallCart) => {
  try {
    fedopsLogger.interactionStarted(Actions.CART_INSTALLATION);
    await installEcomCartIcon({
      editorOptions,
      editorScriptApi,
      instance,
      locale,
      editorSdk,
      fedopsLogger,
      msid,
      editorOrigin,
    });
    await markCartEnabled({
      editorSdk,
      editorScriptApi,
      instance,
      fedopsLogger,
      isFirstInstall: editorOptions?.firstInstall,
      msid,
      editorOrigin,
    });
    fedopsLogger.interactionEnded(Actions.CART_INSTALLATION);
  } catch (e) {
    const errorMessage = `${
      e?.message ? e.message : JSON.stringify(e)
    } - Action ${Actions.CART_INSTALLATION}`;
    reportBi({
      msid,
      origin: editorOrigin,
      isFirstInstall: editorOptions?.firstInstall,
      actionType: Actions.CART_INSTALLATION,
      actionSucceed: false,
      errorMessage,
    });
    throw new Error(errorMessage);
  }
};

export const markCartEnabled = async ({
  editorScriptApi,
  isFirstInstall,
  instance,
  msid,
  editorOrigin,
  fedopsLogger,
}: MarkCartEnabled) => {
  try {
    fedopsLogger.interactionStarted(Actions.MARK_CART_ENABLED);
    const isCartEnabled: boolean | undefined =
      await getIsCartEnabledBusinessProperty(editorScriptApi, instance);

    // undefined meaning this flag never initiate, and we should initiate for first install to true
    if (isFirstInstall && isCartEnabled === undefined) {
      await updateIsCartEnabledBusinessProperty(
        editorScriptApi,
        instance,
        true,
      );
    }
    fedopsLogger.interactionEnded(Actions.MARK_CART_ENABLED);
  } catch (e) {
    const errorMessage = `${
      e?.message ? e.message : JSON.stringify(e)
    } - instance ${instance}`;
    reportBi({
      msid,
      origin: editorOrigin,
      isFirstInstall,
      actionType: Actions.MARK_CART_ENABLED,
      actionSucceed: false,
      errorMessage,
    });
    console.log('mark enabled cart failed', e);
  }
};

export const migrateCart = async ({
  editorSdk,
  editorScriptApi,
  instance,
}: MigrateCartParams) => {
  try {
    await updateIsCartEnabledBusinessProperty(editorScriptApi, instance, true);
  } catch (e) {
    const errorMessage = `${
      e?.message ? e.message : JSON.stringify(e)
    } | migrateCart - updateIsCartEnabledBusinessProperty - errorCode: 8ad89f0b-96c9-4538-9158-329418318132`;
    throw new Error(errorMessage);
  }

  const isCartInstalled = !!(await isEcomCartInstalled(editorSdk));

  if (!isCartInstalled) {
    try {
      await addComponents(editorSdk, [EcomComponent.CART]);
    } catch (e) {
      const errorMessage = `${
        e?.message ? e.message : JSON.stringify(e)
      } | migrateCart - addComponents - errorCode: 4589f0b-96c9-4538-9158-329418398312`;
      throw new Error(errorMessage);
    }
  }

  console.log('Cart Migration Success');
};
